import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useTranslation } from 'react-i18next'
import { t as lt } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useResizeDetector } from 'react-resize-detector'
import { FieldArray, Form, Formik } from 'formik'

import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
// import Typography from '@mui/material/Typography'

import StatField from '@/components/formik/fields/StatField'

import { generateCsv } from '@/utils/helpers'
import { useDebouncedProgress } from '@/hooks/useDebouncedProgress'
import SelectInput from '@/components/ui/inputs/SelectInput'
import { MeasurementUnitSystem, useMeasurementUnitSystem } from '@/hooks/useMeasurementUnitSystem'
import { parseMeasurementValue, toMeasurementUnit } from '@/utils/unit-system'

// Function to convert labels to translation keys
export function labelToTranslationKey(label: string) {
  return 'page.liveConsultation.mesures.values.' + _.deburr(label.replace(/[\s-]+/g, '')).toLowerCase()
}

const LinearProgressContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  width: '100%',
  zIndex: theme.zIndex.appBar + 1,
}))

export type MesuresProps = {
  pcStatus?: boolean
  isStreamer?: boolean
  mesures: {
    label: string
    unit: string
    value: string | number | null
    order: number
  }[]
  onMesuresChange?: (mesures: MesuresProps['mesures']) => void
  onMesuresSave?: (csv: string) => void
  onOpenDevices?: () => void
  readOnly?: boolean
}

export function Mesures(props: MesuresProps) {
  const { mesures, onMesuresChange, onMesuresSave, readOnly = false } = props
  const { t } = useTranslation()
  // The unit labels are Lingui messages (the i18next `t` has no such keys and returned the English text).
  useLingui()
  const { width, ref } = useResizeDetector()

  const [currentField, setCurrentField] = useState<string>()
  const { debouncing, setDebouncing, progress } = useDebouncedProgress()
  const { unitSystem, setUnitSystem } = useMeasurementUnitSystem()

  const initialValues = useMemo(
    () => ({
      mesures: mesures.map((mesure) => ({ ...mesure, value: parseMeasurementValue(mesure.unit, mesure.value, unitSystem) })),
    }),
    [mesures, unitSystem]
  )

  const debouncedHandleShowDebouncingProgress = useDebouncedCallback((field: string) => {
    setCurrentField(field)
    setDebouncing(true)
  }, 300)

  const debouncedHandleHideDebouncingProgress = useDebouncedCallback(
    () => {
      if (debouncing) setDebouncing(false)
    },
    300,
    { leading: true }
  )

  const debouncedUpdateMesures = useDebouncedCallback((mesures: MesuresProps['mesures']) => {
    setDebouncing(false)
    onMesuresChange?.(
      mesures.map((mesure) => ({ ...mesure, value: parseMeasurementValue(toMeasurementUnit(mesure.unit, unitSystem), mesure.value, 'metric') }))
    )
  }, 3000)

  const handleSaveMesures = (values: typeof initialValues) => () => {
    const data: Record<string, string> = _.reduce(
      values.mesures,
      (result: Record<string, string>, mesure) => {
        result[mesure.label] = !_.isNull(mesure.value) && !_.isUndefined(mesure.value) ? `${mesure.value} ${mesure.unit}` : ''
        return result
      },
      {}
    )
    data[t('page.liveConsultation.mesures.csvHeaders.updatedAt')] = dayjs().format('DD/MM/YYYY HH:mm:ss')
    const fields = [
      ..._.map(values.mesures, (mesure) => mesure.label),
      t('page.liveConsultation.mesures.csvHeaders.comment'),
      t('page.liveConsultation.mesures.csvHeaders.updatedAt'),
    ]
    const csv = generateCsv([data], fields)
    onMesuresSave?.(csv)
  }

  let containerWidth = width || 0
  let mesureNumberColumns = 1
  switch (true) {
    case containerWidth > 600:
      mesureNumberColumns = 3
      break
    case containerWidth > 300:
      mesureNumberColumns = 2
      break
    default:
      break
  }

  return (
    <Container maxWidth="md" sx={{ pt: '20px', pb: '20px' }}>
      <Box ref={ref}>
        <Formik enableReinitialize initialValues={initialValues} onSubmit={() => {}}>
          {({ handleSubmit, values }) => (
            <Form onSubmit={handleSubmit}>
              {!readOnly && (
                <Button onClick={handleSaveMesures(values)} variant="contained">
                  {t('page.liveConsultation.mesures.save')}
                </Button>
              )}
              {/* <Typography variant="h6" component="div" sx={{ fontSize: '18px', fontWeight: 500 }}>
          {t('page.liveConsultation.mesures.title', { context: pcStatus ? 'connected' : 'deconnected' })}
        </Typography> */}

              <Grid container rowSpacing={2} sx={{ mt: '5px' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12 / mesureNumberColumns}>
                    <SelectInput
                      fullWidth
                      label={lt`Unit system`}
                      options={[
                        { label: lt`Metric`, value: 'metric' },
                        { label: lt`Imperial`, value: 'imperial' },
                      ]}
                      value={unitSystem}
                      onChange={(e) => setUnitSystem(e.target.value as MeasurementUnitSystem)}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <FieldArray
                    name="mesures"
                    render={() => (
                      <Grid container spacing={2}>
                        {_.map(values.mesures, (mesure, index) => (
                          <Grid key={`${mesure.label} - ${index}`} item xs={12 / mesureNumberColumns}>
                            <Box sx={{ position: 'relative' }}>
                              {currentField === `mesures[${index}].value` && debouncing && (
                                <LinearProgressContainer>
                                  <LinearProgress variant="determinate" value={progress} sx={{ transform: 'rotate(180deg)' }} />
                                </LinearProgressContainer>
                              )}
                              <StatField
                                key={index}
                                fontSize="medium"
                                label={`${t(labelToTranslationKey(mesure.label))} ( ${toMeasurementUnit(mesure.unit, unitSystem)} )`}
                                name={`mesures[${index}].value`}
                                inputProps={{
                                  type: 'string',
                                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    debouncedHandleHideDebouncingProgress()
                                    debouncedHandleShowDebouncingProgress(`mesures[${index}].value`)
                                    debouncedUpdateMesures(_.unionBy([{ ...mesure, value: e.target.value }], values.mesures, 'label'))
                                  },
                                }}
                                readOnly={readOnly}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </Container>
  )
}

export default Mesures
